import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { RetailerTypeResponse, RetailerTypeRequest, RetailerTypesService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';

@Component({
    selector: 'app-edit-retailer-type',
    templateUrl: './edit-retailer-type.component.html',
    styleUrl: './edit-retailer-type.component.scss',
    host: {
        class: 'app-edit-retailer-type'
    },
    standalone: false
})
export class EditRetailerTypeComponent extends BaseViewComponent implements OnInit {

  retailerTypeId: number = 0;
  retailerType: RetailerTypeResponse = {};

  filterOperator = FilterOperator;
  retailerTypesList: any[] = [];
  retailerTypesTotalRecords: number = 0;
  retailerTypesRowsPerPageOption: number = 10;

  public retailerTypeForm: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    code: ['', [Validators.required, Validators.maxLength(50)]],
    hasParentRetailerType: ['false'],
    retailerType: [[]]
  },
    { validators: this.conditionalValidators() });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private retailerTypesService: RetailerTypesService,
    private fb: FormBuilder
  ) { super(); }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.route.params.subscribe(async params => {
      this.retailerTypeId = params['retailerTypeId'];

      if (this.retailerTypeId) {
        await this.getRetailerTypeId();
      }
    });
  }

  conditionalValidators(): ValidatorFn {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const hasParentRetailerType = formGroup.get('hasParentRetailerType')?.value;
      const retailerType = formGroup.get('retailerType')?.value;

      const hasSelectedRetailerType = Array.isArray(retailerType)
        ? retailerType.length > 0
        : !!retailerType;

      if (hasParentRetailerType === 'true' && !hasSelectedRetailerType) {
        return { requiredRetailerType: true };
      }

      return null;
    };
  }

  isRetailerTypeRequired(): boolean {
    return this.retailerTypeForm.get('hasParentRetailerType')?.value === 'true';
  }

  async getRetailerTypeId() {
    this.retailerType = await firstValueFrom(this.retailerTypesService.apiRetailerTypesIdGet(this.retailerTypeId));
    this.initForm();
    await this.getRetailerTypes({ pageIndex: 1, search: '' });
  }

  onLazyLoadRetailerTypes(event: any) {
    this.getRetailerTypes(event);
  }

  async getRetailerTypes(event: any) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));
    const pageSize = Number(event?.pageSize) || this.retailerTypesRowsPerPageOption;

    this.retailerTypesRowsPerPageOption = pageSize;
    const payload : FilterRequestForCombo = {
      pageNumber: activePage,
      pageSize: this.retailerTypesRowsPerPageOption
    }

    if (search) {
      payload.textFilter = search;
    }

    const retailerTypesPage = await firstValueFrom(this.retailerTypesService.apiRetailerTypesFilterComboPost(payload));

    // prevent adding itself as parent
    this.retailerTypesList = (retailerTypesPage.results ?? []).filter(c => c.id != this.retailerTypeId);

    this.retailerTypesTotalRecords = retailerTypesPage.totalCount ?? 0;

    if (this.retailerType?.parent?.id && this.retailerType.parent.id != this.retailerTypeId) {
      const exists = this.retailerTypesList.some((c: any) => c.id == this.retailerType.parent?.id);
      if (!exists) {
        const current = await firstValueFrom(this.retailerTypesService.apiRetailerTypesComboValueIdGet(this.retailerType.parent?.id))
        this.retailerTypesList = [current, ...this.retailerTypesList];
      }
    }

  }

  getRetailerTypesTotalPages(): number {
    const total = Number(this.retailerTypesTotalRecords) || 0;
    const pageSize = Number(this.retailerTypesRowsPerPageOption) || 10;

    return Math.max(1, Math.ceil(total / pageSize));
  }

  initForm() {
    this.retailerTypeForm.controls['name'].setValue(this.retailerType.name);
    this.retailerTypeForm.controls['code'].setValue(this.retailerType.code);

    if (this.retailerType.parentId) {
      this.retailerTypeForm.controls['hasParentRetailerType'].setValue('true');
      this.retailerTypeForm.controls['retailerType'].setValue([this.retailerType.parentId]);
    } else {
      this.retailerTypeForm.controls['hasParentRetailerType'].setValue('false');
      this.retailerTypeForm.controls['retailerType'].setValue([]);
    }

    this.retailerTypeForm.get('hasParentRetailerType')?.valueChanges.subscribe((hasParent) => {
      if (hasParent === 'false') {
        this.retailerTypeForm.controls['retailerType'].setValue([]);
      }
    });
  }

  async submitForm() {
    if (this.retailerTypeForm.valid) {

      const payload: RetailerTypeRequest = {
        name: this.retailerTypeForm.controls['name'].value,
        code: this.retailerTypeForm.controls['code'].value,
        parentId: this.retailerTypeForm.controls['retailerType'].value?.[0]
      };

      await firstValueFrom(this.retailerTypesService.apiRetailerTypesUpdateIdPut(this.retailerTypeId, payload));

      this.router.navigate(['/retailer-types', this.retailerTypeId]);
    }
  }
}
